import { useEffect } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarHeart, Loader2, Phone } from "lucide-react";
import { imgSet, imgUrl, servicesFor, useContent } from "@/lib/content";
import { PageLoader } from "@/pages/Home";
import { useServices } from "@/lib/useServices";
import { BookLink, openFresha, useFreshaFor } from "@/lib/fresha";

const ServiceGroup = ({ cat, onBook }) => (
  <div className="look-group" data-testid={`look-group-${cat.id}`}>
    <h2>{cat.label}</h2>
    {cat.items.map((s) => (
      <button className="price-row sheet-row" key={s.key} onClick={() => onBook(s.key)} data-testid="look-service-row">
        <span>{s.name}<small className="dur">{s.duration}</small></span>
        <span className="row-right"><strong>{s.price}</strong><span className="row-book">Book <ArrowRight size={12} /></span></span>
      </button>
    ))}
  </div>
);

const Others = ({ slug, items }) => (
  <div className="look-others" data-testid="look-others">
    <p className="eyebrow">More inspiration</p>
    <div className="look-chips">
      {items.filter((g) => g.slug !== slug).map((g) => (
        <Link key={g.slug} to={`/inspiration/${g.slug}`} className="pill" data-testid={`look-chip-${g.slug}`}>{g.caption}</Link>
      ))}
    </div>
  </div>
);

export default function Inspiration() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { data: content } = useContent();
  const { data, isLoading } = useServices();
  const freshaFor = useFreshaFor();
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!content) return <PageLoader />;
  const look = content.gallery.items.find((g) => g.slug === slug);
  if (!look) return <Navigate to="/" replace />;

  const groups = servicesFor(look, data?.categories || []);
  const book = (key) => (freshaFor ? openFresha(freshaFor(key)) : nav(`/?book=${encodeURIComponent(key)}`));

  return (
    <div className="wm has-bar look" data-testid="inspiration-page">
      <header className="look-top">
        <Link to="/#gallery" className="look-back" data-testid="look-back-link"><ArrowLeft size={18} /> Back</Link>
        <Link to="/" className="logo logo-black" data-testid="look-logo"><img src="/brand/logo-black.png" alt="Wax Moi Day Spa" width="835" height="240" /></Link>
      </header>
      <section className="look-hero">
        <img src={imgUrl(look.image, 1200)} srcSet={imgSet(look.image)} sizes="100vw" alt={look.caption} fetchPriority="high" />
        <div className="look-hero-text">
          <p className="eyebrow">A little inspiration</p>
          <h1 data-testid="look-title">{look.caption}</h1>
          <p>{look.intro}</p>
        </div>
      </section>
      <main className="wrap look-body">
        {isLoading && <p className="muted-sm center" data-testid="look-loading"><Loader2 size={16} className="spin" /> Loading services…</p>}
        {groups.map((c) => <ServiceGroup key={c.id} cat={c} onBook={book} />)}
        <p className="price-note confirm">Prices in BSD. Please confirm current pricing when booking.</p>
        <Others slug={slug} items={content.gallery.items} />
      </main>
      <div className="mobilebar" data-testid="look-action-bar">
        <BookLink as={Link} internal="/#book" className="btn gold" testId="look-book-button"><CalendarHeart size={17} /> Book</BookLink>
        <a className="btn dark" href={`tel:${content.contact.phone_call}`} data-testid="look-call-button"><Phone size={17} /> Call</a>
      </div>
    </div>
  );
}
